import * as React from "react";
import { getQuote, convertQuote, expireQuote } from "../../../backend-services";
import { IQuote } from "../../../@types";
import PageLoader from "../../../components/common/PageLoader";
import { useNavigate, useParams } from "react-router-dom";
import { Annotation } from "../../../@types/annotation";
import { ViewQuote } from "../../../components/Sales/quotes/View/view";
import { showToast } from "../../../utils/toastUtils";
import { formatMoney } from "../../../utils";
import { MobileDataItem } from "../invoices";
import { useCompanyStore } from "../../../state/useCompanyStore";

const ViewQuotePage: React.FC = () => {
  const { quoteNo } = useParams();
  if (!quoteNo) {
    throw new Error("Quote Id is missing");
  }
  const userStore = useCompanyStore();
  const isRetail = userStore.isRetailBusiness();
  const [quote, setQuote] = React.useState<IQuote>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isRefunding, setIsRefunding] = React.useState(false);
  const [isConverting, setIsConverting] = React.useState(false);
  const [annotations, setAnnotations] = React.useState<Annotation[]>([]);
  const navigate = useNavigate();

  const handleConvertQuote = React.useCallback(async () => {
    setIsConverting(true);
    try {
      const convertedQuote = await convertQuote({ id: quoteNo });
      const invoiceId = convertedQuote.id;
      setQuote(convertedQuote);
      showToast("Quote converted to Invoice successfully!", "success");
      setTimeout(() => {
        navigate(`/invoices/${invoiceId}/view`);
      }, 5000);
    } catch (error: any) {
      showToast("Unable to Convert quote.", "error");
    } finally {
      setIsRefunding(false);
    }
  }, [quoteNo]);

  const handleMarkAsExpired = React.useCallback(async () => {
    setIsConverting(true);
    try {
      const quote = await expireQuote({ id: quoteNo });
      fetchQuote();
      showToast("Quote marked as Expired successfully!", "success");
    } catch (error: any) {
      showToast("Unable to mark as expired", "error");
    } finally {
      setIsRefunding(false);
      setIsConverting(false);
    }
  }, [quoteNo]);

  const fetchQuote = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getQuote(quoteNo);
      if (data && typeof data === "object") {
        setQuote(data);
        if (data.metadata && data.metadata.annotations) {
          setAnnotations(data.metadata.annotations);
        }
      } else {
        throw new Error("Invalid quote data received");
      }
    } catch (error) {
      showToast("Failed to load quote", "error");
    } finally {
      setIsLoading(false);
    }
  }, [quoteNo]);

  React.useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  let mobileData: MobileDataItem[] = [];
  let data: any[] = [];
  if (quote && quote.inventories && isRetail) {
    // Retail business, show inventories data
    data = quote.inventories.flatMap((inv: any) => {
      if (Array.isArray(inv.variations) && inv.variations.length > 0) {
        return inv.variations.map((variation: any) => ({
          items: `${inv.name} - ${variation.type}`,
          quantity: variation?.quantity || 0,
          rate: formatMoney(variation?.price || "0"),
          amount: formatMoney(
            `${
              parseInt(variation.price || "0") *
              parseInt(variation.quantity || "0")
            }`,
            quote.currency,
          ),
        }));
      }
      return {
        items: inv.name,
        quantity: inv?.quantity || 0,
        rate: formatMoney(`${parseInt(inv.rate || "0")}`, quote.currency),
        amount: formatMoney(
          `${parseInt(inv.rate || "0") * parseInt(inv.quantity || "0")}`,
          quote.currency,
        ),
      };
    });
  } else if (quote && quote.services && !isRetail) {
    // Not a retail business, show services data
    data = quote.services.map((ser: any) => ({
      services: ser.name,
      // duration: ser?.duration || "N/A",
      serviceCharge: formatMoney(
        `${parseInt(ser.price || "0")}`,
        quote.currency,
      ),
    }));

    // Ensure inventories exist before calling flatMap
    if (quote.inventories && Array.isArray(quote.inventories)) {
      mobileData = quote.inventories.flatMap((inv: any) => {
        if (Array.isArray(inv.variations) && inv.variations.length > 0) {
          return inv.variations.map((variation: any) => ({
            name: inv.name + " - " + variation.type,
            quantity: variation.quantity,
            amount: formatMoney(
              `${parseInt(variation.rate) * parseInt(variation.quantity)}`,
              quote.currency,
            ),
          }));
        } else {
          return [
            {
              name: inv.name,
              quantity: inv.quantity,
              amount: formatMoney(
                `${parseInt(inv.rate) * parseInt(inv.quantity)}`,
                quote.currency,
              ),
            },
          ];
        }
      });
    } else if (quote && quote.services && !isRetail) {
      data = quote.services.map((ser: any) => ({
        services: ser.name,
        // duration: ser?.duration || "N/A",
        serviceCharge: formatMoney(
          `${parseInt(ser.price || "0")}`,
          quote.currency,
        ),
      }));

      mobileData = quote.services.map((ser: any, i: any) => ({
        name: ser.name,
        duration: ser?.duration,
        serviceCharge: formatMoney(`${parseInt(ser.price)}`, quote.currency),
      }));
    }
  }

  return (
    <div>
      {isLoading || data.length === 0 ? (
        <PageLoader></PageLoader>
      ) : (
        <ViewQuote
          quote={quote}
          annotations={annotations}
          isRefunding={isRefunding}
          handleConvertQuote={handleConvertQuote}
          handleMarkAsExpired={handleMarkAsExpired}
          customerId={""}
          data={data}
        />
      )}
    </div>
  );
};
export { ViewQuotePage };
